import { inject, Injectable, signal } from '@angular/core';
import {
  ChatMessage,
  ChatMessageBuilder,
  HandshakeMode,
  HandshakeResponse,
  SurveyLoginType,
  SurveyResolverStartPayload,
} from '@hyper-inferno/survey-elements-core-utils';

// Import your handlers (Assuming these are also available in your JS libs)
import { AuthService } from '../auth.service';
import { IBotService } from '@hyper-inferno/survey-elements-core-ui';
import { SurveyBotController } from './survey-bot.controller';
import { from, Observable } from 'rxjs';
import { SurveyListOption } from '@hyper-inferno/survey-elements-core';
import { RxDBProviderService } from './rx-db.service';

@Injectable({
  providedIn: 'root',
})
export class SurveyBotOffline implements IBotService {
  validation_enabled = signal(true);
  private controller = inject(SurveyBotController);

  private auth = inject(AuthService);

  private rxdb = inject(RxDBProviderService);


  /**
   * Handshake: Creates the local record in RxDB
   */
  async handshake(
    payload: SurveyResolverStartPayload,
  ): Promise<HandshakeResponse> {
    return this.controller
      .getHandshakeToken(
        {
          surveyId: payload.surveyId,
          loginType: SurveyLoginType.SELF_SERVE,
          sessionId: payload.sessionId,
          invitationId: payload.invitationId,
        },
        '',
      )
      .catch((err) => {
        return {
          mode: HandshakeMode.ERROR,
          errorMessage: new ChatMessageBuilder()
            .setSurveyError(err.message)
            .build(),
          serverTime: new Date().getMilliseconds(),
        };
      });
  }

  /**
   * Start Transaction: Renders the first page using the Engine
   */
  async startSurveyTransaction(token: string): Promise<ChatMessage> {
    const decodedToken = JSON.parse(token);
    const sessionId = decodedToken.sessionId;
    const surveyId = decodedToken.surveyId;
    const params = decodedToken.params;
    const user = this.auth.getUserIdManual();

    return this.controller.startSession(surveyId, sessionId, params, user);
  }

  /**
   * Send Answer: Processes the submission through the Engine logic
   */
  async sendAnswerBot(message: ChatMessage): Promise<ChatMessage> {
    const sessionId = message.sender;

    if (!sessionId) {
      throw new Error('No Session Context');
    }
    return this.controller.handleMessage(message, sessionId);
  }

  fetchAfmOptions(
    props: any,
    pageValue: any,
  ): Observable<SurveyListOption[]> {
    return from(
      this.controller.getDynamicOptions({
        props,
        page_value: pageValue
      })
    );
  }

  /**
 * Retrieves an attachment stored in RxDB for a given survey and relative media path,
 * returning a browser Blob Object URL (`blob:http://...`) for direct use in <img>, <video>, etc.
 */
  public async getAttachmentUrl(
    surveyId: number,
    mediaPath: string,
  ): Promise<string | null> {
    if (!mediaPath) return null;

    try {
      const db = await this.rxdb.init();

      // 1. Fetch latest survey version document
      const bundleDoc = await db.survey_versions
        .findOne({
          selector: { survey_id: Number(surveyId) },
          sort: [{ version_number: 'desc' }],
        })
        .exec();

      if (!bundleDoc) {
        console.warn(`Survey version for survey_id ${surveyId} not found offline.`);
        return null;
      }

      // 2. Exact match attempt
      let attachment = bundleDoc.getAttachment(mediaPath);

      // Fallback: If mediaPath contains full URL or path, strip leading paths to match filename ID
      if (!attachment && mediaPath.includes('/')) {
        const filename = mediaPath.split('/').pop();
        if (filename) {
          attachment = bundleDoc.getAttachment(filename);
        }
      }

      if (!attachment) {
        console.warn(`Attachment not found at path "${mediaPath}" for survey ${surveyId}.`);
        return null;
      }

      // 3. Fetch binary Blob from RxDB IndexedDB/Dexie
      const blobData = await attachment.getData();

      // 4. Create browser Object URL
      return URL.createObjectURL(blobData);
    } catch (err) {
      console.error(`Error loading attachment "${mediaPath}":`, err);
      return null;
    }
  }

  public revokeAttachmentUrl(url: string | null): void {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }
}
